import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { financeApi, propertyApi, insightsApi, taxApi, type PropertyDto, type FinancialSummaryDto } from "../api.js";
import { Card, EmptyState, ErrorBanner, MetricCard, Money, PageHeader, Spinner } from "../components/ui.js";

interface DashboardTaxPosition {
  countryCode: string;
  taxPeriodId: string;
  profitBeforeTax: number;
  estimatedIncomeTax: number | null;
}

export function readPortfolioHealth(value: unknown): { healthScore?: number; healthLabel?: string } {
  if (!value || typeof value !== "object") return {};
  const health = value as Record<string, unknown>;
  return {
    healthScore: typeof health.healthScore === "number" ? health.healthScore : undefined,
    healthLabel: typeof health.healthLabel === "string" ? health.healthLabel : undefined,
  };
}

export function DashboardPage() {
  const [properties, setProperties] = useState<PropertyDto[] | null>(null);
  const [summary, setSummary] = useState<FinancialSummaryDto | null>(null);
  const [health, setHealth] = useState<{ healthScore?: number; healthLabel?: string } | null>(null);
  const [taxPosition, setTaxPosition] = useState<DashboardTaxPosition | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [propsRes, sumRes, healthRes, taxRes] = await Promise.all([
        propertyApi.list(),
        financeApi.summary(),
        insightsApi.healthPortfolio(),
        taxApi.summary(),
      ]);
      if (!propsRes.ok) setError(propsRes.error?.errorCode ?? "Failed to load properties");
      else setProperties(propsRes.data ?? []);
      if (!sumRes.ok) setSummaryError(sumRes.error?.errorCode ?? "Financial summary unavailable");
      else if (sumRes.data) setSummary(sumRes.data);
      if (!healthRes.ok) setHealthError(healthRes.error?.errorCode ?? "Portfolio health unavailable");
      else if (healthRes.data) setHealth(readPortfolioHealth(healthRes.data));
      if (!taxRes.ok) setTaxError(taxRes.error?.errorCode ?? "Tax position unavailable");
      else if (taxRes.data) setTaxPosition(taxRes.data as DashboardTaxPosition);
    })();
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (properties === null) return <Spinner label="Loading dashboard…" />;

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="Portfolio overview"
        title="Good to see you."
        description="A clear view of your properties, financial position and what deserves attention next."
        actions={<Link to="/properties/new" className="btn btn-primary">Add a property</Link>}
      />
      <div className="metric-grid dashboard-metrics">
        <MetricCard
          label="Properties"
          value={properties.length}
          hint={properties.length === 1 ? "Property in your portfolio" : "Properties in your portfolio"}
        />
        <MetricCard
          label="Net position"
          value={summaryError ? "Unavailable" : summary ? <Money amount={summary.netPosition} currency={summary.currency} /> : "—"}
          hint={summaryError ?? "Income less recorded expenses"}
          tone={summary && summary.netPosition >= 0 ? "positive" : "attention"}
        />
        <MetricCard
          label="Portfolio health"
          value={healthError ? "Unavailable" : health?.healthScore ?? "—"}
          hint={healthError ?? health?.healthLabel ?? "Waiting for enough information"}
        />
        <MetricCard
          label="Profit before tax"
          value={taxError ? "Unavailable" : taxPosition
            ? <Money amount={taxPosition.profitBeforeTax} currency={taxPosition.countryCode === "NZ" ? "NZD" : undefined} />
            : "—"}
          hint={taxError ?? (taxPosition
            ? `${taxPosition.taxPeriodId} · ${taxPosition.estimatedIncomeTax === null ? "Estimated tax unavailable" : "Estimated tax available"}`
            : "Waiting for tax information")}
          tone={taxPosition && taxPosition.profitBeforeTax >= 0 ? "positive" : "attention"}
        />
      </div>
      <Card title="Your properties" description="The current shape of your rental portfolio.">
        {properties.length === 0
          ? <EmptyState
              title="Add your first property"
              message="Start with the property basics, then connect income and expenses as they happen."
              action={<Link to="/properties/new" className="btn btn-primary btn-sm">Add property</Link>}
            />
          : <ul className="prop-list">
              {properties.map((p) => (
                <li key={p.id}><Link to={`/properties/${p.id}`}>{p.name}</Link> — <span className="muted">{p.status}</span></li>
              ))}
            </ul>}
        {properties.length > 0 && <Link to="/properties" className="btn btn-secondary btn-sm">Manage properties</Link>}
      </Card>

      <Card title="Money in and out" description="Your recorded portfolio activity at a glance.">
        {summaryError
          ? <ErrorBanner message={summaryError} />
          : summary
          ? <dl className="summary-grid">
              <dt>Total income</dt><dd><Money amount={summary.totalIncome} currency={summary.currency} /></dd>
              <dt>Total expenses</dt><dd><Money amount={summary.totalExpenses} currency={summary.currency} /></dd>
              <dt>Net position</dt><dd><Money amount={summary.netPosition} currency={summary.currency} /></dd>
            </dl>
          : <Spinner />}
        <Link to="/finance" className="btn btn-secondary btn-sm">Finance details</Link>
      </Card>

      <Card title="What needs attention" description="Nettstay's current view based on the information available.">
        {healthError
          ? <ErrorBanner message={healthError} />
          : health
          ? <div className="health-display"><span className="health-score">{health.healthScore ?? "—"}</span><span className="health-label">{health.healthLabel ?? "Unknown"}</span></div>
          : <Spinner />}
        <Link to="/insights" className="btn btn-secondary btn-sm">View insights</Link>
      </Card>
    </div>
  );
}
